import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface LightboxImage {
  src: string;
  alt?: string;
}

interface ImageLightboxProps {
  images: LightboxImage[];
  currentIndex: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  aiIdPrefix?: string;
}

function clampIndex(index: number, total: number): number {
  if (total <= 0) {
    return 0;
  }

  return Math.min(total - 1, Math.max(0, index));
}

export function ImageLightbox({
  images,
  currentIndex,
  onClose,
  onIndexChange,
  aiIdPrefix = 'image-lightbox',
}: ImageLightboxProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const isOpen = currentIndex !== null && images.length > 0;
  const activeIndex = isOpen && currentIndex !== null ? clampIndex(currentIndex, images.length) : 0;
  const activeImage = images[activeIndex];
  const hasMultipleImages = images.length > 1;
  const canGoPrevious = activeIndex > 0;
  const canGoNext = activeIndex < images.length - 1;

  const goToIndex = useCallback(
    (nextIndex: number) => {
      if (images.length === 0) {
        return;
      }

      onIndexChange(clampIndex(nextIndex, images.length));
    },
    [images.length, onIndexChange],
  );

  const goPrevious = useCallback(() => {
    if (canGoPrevious) {
      goToIndex(activeIndex - 1);
    }
  }, [activeIndex, canGoPrevious, goToIndex]);

  const goNext = useCallback(() => {
    if (canGoNext) {
      goToIndex(activeIndex + 1);
    }
  }, [activeIndex, canGoNext, goToIndex]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const animationFrame = window.requestAnimationFrame(() => {
      overlayRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(animationFrame);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || currentIndex === null) {
      return;
    }

    const nextIndex = clampIndex(currentIndex, images.length);
    if (nextIndex !== currentIndex) {
      onIndexChange(nextIndex);
    }
  }, [currentIndex, images.length, isOpen, onIndexChange]);

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    event.stopPropagation();

    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goPrevious();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goNext();
    }
  }

  if (!isOpen || !activeImage || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={overlayRef}
      tabIndex={-1}
      className="fixed inset-0 z-[100] bg-slate-950/95 text-white outline-none"
      role="dialog"
      aria-modal="true"
      aria-label="가는길 이미지 크게 보기"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      data-ai-id={aiIdPrefix}
    >
      <div
        className="pointer-events-none absolute left-1/2 top-4 z-20 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm font-semibold text-white"
        data-ai-id={`${aiIdPrefix}-counter`}
      >
        <span data-ai-id={`${aiIdPrefix}-counter-text`}>
          {activeIndex + 1} / {images.length}
        </span>
      </div>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        className="absolute right-4 top-4 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
        aria-label="이미지 크게 보기 닫기"
        data-ai-id={`${aiIdPrefix}-close-button`}
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" data-ai-id={`${aiIdPrefix}-close-button-icon`}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" data-ai-id={`${aiIdPrefix}-close-button-icon-path`} />
        </svg>
      </button>

      {hasMultipleImages ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            goPrevious();
          }}
          className="absolute left-3 top-1/2 z-20 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70 disabled:cursor-not-allowed disabled:opacity-35 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 sm:left-5"
          aria-label="이전 가는길 이미지"
          disabled={!canGoPrevious}
          data-ai-id={`${aiIdPrefix}-previous-button`}
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" data-ai-id={`${aiIdPrefix}-previous-button-icon`}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m15 19-7-7 7-7" data-ai-id={`${aiIdPrefix}-previous-button-icon-path`} />
          </svg>
        </button>
      ) : null}

      <div
        className="flex h-full w-full flex-col px-4 pb-4 pt-16"
        onClick={(event) => event.stopPropagation()}
        data-ai-id={`${aiIdPrefix}-content`}
      >
        <div className="flex min-h-0 flex-1 items-center justify-center" data-ai-id={`${aiIdPrefix}-image-stage`}>
          <img
            src={activeImage.src}
            alt={activeImage.alt || `가는길 이미지 ${activeIndex + 1}`}
            className="max-h-full max-w-full select-none rounded-xl object-contain shadow-2xl shadow-black/40"
            draggable={false}
            data-ai-id={`${aiIdPrefix}-image`}
          />
        </div>

        {hasMultipleImages ? (
          <div className="mt-4 overflow-x-auto rounded-2xl border border-white/15 bg-white/5 p-2" data-ai-id={`${aiIdPrefix}-thumbnails-panel`}>
            <div className="flex gap-2" data-ai-id={`${aiIdPrefix}-thumbnails-track`}>
              {images.map((image, index) => {
                const isActive = index === activeIndex;

                return (
                  <button
                    key={`${image.src}-${index}`}
                    type="button"
                    onClick={() => goToIndex(index)}
                    className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border bg-black/25 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                      isActive ? 'border-white opacity-100' : 'border-white/20 opacity-60 hover:opacity-90'
                    }`}
                    aria-label={`가는길 이미지 ${index + 1} 보기`}
                    aria-current={isActive ? 'true' : undefined}
                    data-ai-id={`${aiIdPrefix}-thumbnail-button`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt || `가는길 이미지 ${index + 1} 썸네일`}
                      className="h-full w-full object-cover"
                      draggable={false}
                      data-ai-id={`${aiIdPrefix}-thumbnail-image`}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {hasMultipleImages ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            goNext();
          }}
          className="absolute right-3 top-1/2 z-20 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70 disabled:cursor-not-allowed disabled:opacity-35 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 sm:right-5"
          aria-label="다음 가는길 이미지"
          disabled={!canGoNext}
          data-ai-id={`${aiIdPrefix}-next-button`}
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" data-ai-id={`${aiIdPrefix}-next-button-icon`}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" data-ai-id={`${aiIdPrefix}-next-button-icon-path`} />
          </svg>
        </button>
      ) : null}
    </div>,
    document.body,
  );
}

export default ImageLightbox;
